import { useEffect } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, AlertTriangle } from "lucide-react";

const Terms = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sections = [
        {
            title: "1. Introduction",
            content: [
                "Welcome to Parakh (\"we,\" \"our,\" or \"Platform\"), an AI-powered UPSC answer evaluation platform operated by Kuberya AI. By accessing or using Parakh at parakh-ai-rho.vercel.app, you agree to be bound by these Terms and Conditions."
            ]
        },
        {
            title: "2. Acceptance of Terms",
            content: [
                "By creating an account, uploading content, or using any of our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree, please discontinue use immediately."
            ]
        },
        {
            title: "3. Eligibility",
            content: [
                "You must be at least 16 years of age to use this Platform",
                "You represent that all information provided during registration is accurate and complete",
                "You are responsible for maintaining the confidentiality of your account credentials"
            ]
        },
        {
            title: "4. Service Description",
            content: [
                "Parakh provides AI-powered evaluation services for UPSC Mains answer sheets, including:",
                "• Automated evaluation of uploaded answer sheets",
                "• Performance analytics and progress tracking",
                "• Model answers and improvement suggestions",
                "• Evaluation history and report downloads",
                "• Access to our UPSC aspirant community"
            ]
        },
        {
            title: "5. User Obligations",
            content: [
                "You agree to:",
                "• Upload only your own answer sheets or content you have rights to use",
                "• Use the Platform solely for legitimate UPSC preparation purposes",
                "• Not attempt to reverse engineer, copy, or replicate our AI evaluation system",
                "• Not upload malicious files, viruses, or harmful content",
                "• Not share your account credentials with others",
                "• Not use the Platform for any illegal or unauthorized purpose",
                "• Respect the intellectual property rights of Parakh and other users"
            ]
        },
        {
            title: "6. Content Ownership and License",
            content: [
                "Your Content: You retain ownership of all answer sheets and content you upload. By uploading content, you grant Parakh a non-exclusive, worldwide license to process, analyze, and store your content for the purpose of providing evaluation services and improving our AI models.",
                "Our Content: All evaluation reports, model answers, feedback, analytics, platform design, and AI algorithms remain the exclusive property of Kuberya AI/Parakh. You may not reproduce, distribute, or commercialize our content without written permission."
            ]
        },
        {
            title: "7. Subscription and Payment Terms",
            content: [
                "Pricing: Current subscription plans are:",
                "• Starter Pack: ₹44/month (100 credits)",
                "• Quarterly Pack: ₹99/quarter (200 credits)",
                "Payment: All fees are in Indian Rupees (INR). Payment must be made through our authorized payment gateways.",
                "Credits: Credits are valid for the duration specified in your chosen plan. Unused credits expire at the end of the validity period and are non-refundable.",
                "Refunds: Due to the instant nature of our digital service, evaluations cannot be reversed once processed. Refunds are provided only in cases of technical errors that prevent service delivery.",
                "Plan Changes: You may upgrade your plan at any time. Downgrades take effect at the next billing cycle."
            ]
        },
        {
            title: "8. Service Availability",
            content: [
                "While we strive for 99.9% uptime, we do not guarantee uninterrupted service. Parakh may be temporarily unavailable due to:",
                "• Scheduled maintenance",
                "• Technical issues beyond our control",
                "• Force majeure events",
                "We are not liable for any loss resulting from service interruptions."
            ]
        },
        {
            title: "9. Evaluation Accuracy Disclaimer",
            content: [
                "While our AI achieves 98% accuracy, evaluations are provided as guidance only. Parakh's evaluations:",
                "• Are based on AI algorithms and may not reflect actual UPSC examiner opinions",
                "• Should be used as one tool among many in your preparation",
                "• Do not guarantee success in actual UPSC examinations",
                "• Should not be considered as official UPSC evaluations"
            ]
        },
        {
            title: "10. Privacy and Data Usage",
            content: [
                "Your use of Parakh is also governed by our Privacy Policy. We collect and process data as described in that policy. By using our service, you consent to such collection and processing."
            ]
        },
        {
            title: "11. Prohibited Activities",
            content: [
                "You may not:",
                "• Use automated systems (bots, scrapers) to access the Platform",
                "• Attempt to gain unauthorized access to our systems",
                "• Upload copyrighted material you don't have rights to use",
                "• Harass, abuse, or harm other users in our community",
                "• Resell or commercialize our evaluation services",
                "• Use the Platform to collect data about other users"
            ]
        },
        {
            title: "12. Account Termination",
            content: [
                "We reserve the right to suspend or terminate your account if you:",
                "• Violate these Terms and Conditions",
                "• Engage in fraudulent activity",
                "• Upload inappropriate or illegal content",
                "• Abuse our systems or staff",
                "Upon termination, you lose access to all credits and evaluation history."
            ]
        },
        {
            title: "13. Intellectual Property",
            content: [
                "All trademarks, logos, and service marks displayed on Parakh are the property of Kuberya AI or respective third parties. You may not use these without prior written permission."
            ]
        },
        {
            title: "14. Limitation of Liability",
            content: [
                "To the maximum extent permitted by Indian law, Kuberya AI/Parakh shall not be liable for:",
                "• Any indirect, incidental, or consequential damages",
                "• Loss of data, profits, or opportunity",
                "• Damages exceeding the amount you paid in the past 3 months"
            ]
        },
        {
            title: "15. Indemnification",
            content: [
                "You agree to indemnify and hold harmless Kuberya AI, its directors, employees, and affiliates from any claims, damages, or expenses arising from:",
                "• Your violation of these Terms",
                "• Your use of the Platform",
                "• Content you upload or submit"
            ]
        },
        {
            title: "16. Modifications to Terms",
            content: [
                "We reserve the right to modify these Terms at any time. Significant changes will be communicated via email or platform notification. Continued use after changes constitutes acceptance of modified Terms."
            ]
        },
        {
            title: "17. Governing Law and Jurisdiction",
            content: [
                "These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in Jabalpur, Madhya Pradesh."
            ]
        },
        {
            title: "18. Severability",
            content: [
                "If any provision of these Terms is found unenforceable, the remaining provisions shall remain in full effect."
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 py-20 px-4 md:px-8">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="bg-white rounded-[2.5rem] shadow-xl p-8 md:p-12"
                >
                    {/* Header */}
                    <div className="text-center mb-16 space-y-4">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 items-center justify-center rounded-full text-indigo-700 font-bold text-sm uppercase tracking-wider mb-4">
                            <ShieldCheck className="w-4 h-4" />
                            Legal & Compliance
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight leading-tight">
                            Terms <span className="text-indigo-600">&</span> Conditions
                        </h1>

                    </div>

                    {/* Content */}
                    <div className="space-y-12">
                        {sections.map((section, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.05 }}
                                className="group"
                            >
                                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 group-hover:text-indigo-600 transition-colors">
                                    {section.title}
                                </h2>
                                <div className="space-y-3">
                                    {section.content.map((point, i) => (
                                        <p key={i} className={`text-gray-600 leading-relaxed ${point.startsWith('•') ? 'pl-4' : ''}`}>
                                            {point}
                                        </p>
                                    ))}
                                </div>
                            </motion.div>
                        ))}

                        {/* Contact Section */}
                        <div className="mt-16 p-8 bg-indigo-50 rounded-3xl border border-indigo-100">
                            <h2 className="text-xl font-bold text-indigo-900 mb-6">19. Contact Information</h2>
                            <p className="text-indigo-700/80 mb-6">For questions about these Terms:</p>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Email Us</span>
                                    <a href="mailto:kuberyaai@gmail.com" className="block text-indigo-900 font-bold hover:text-indigo-600 transition-colors">
                                        kuberyaai@gmail.com
                                    </a>
                                </div>
                                <div className="space-y-2">
                                    <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Call Us</span>
                                    <a href="tel:+919009022058" className="block text-indigo-900 font-bold hover:text-indigo-600 transition-colors">
                                        +91 9009022058
                                    </a>
                                </div>
                                <div className="md:col-span-2 space-y-2">
                                    <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest">Visit Us</span>
                                    <p className="text-indigo-900 font-medium leading-relaxed">
                                        Kuberya-AI Office, Vijay Nagar, SBI Chowk,<br />
                                        Jabalpur, Madhya Pradesh, India
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Footer Note */}
                        <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl mt-8">
                            <AlertTriangle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-gray-500 italic">
                                By continuing to use our services, you acknowledge that you have read and understood these terms in their entirety.
                            </p>
                        </div>

                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default Terms;
